import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WorkOrder } from '../../types/workOrder';
import type {
  RoastExtraColumnKey,
  RoastProfileRow,
  RoastStageState,
} from '../../pages/roasts/component/types';
import { RoastProfileChart } from '../../pages/roasts/component/RoastProfileChart';
import {
  calculateChartData,
  calculatePhaseAreas,
  calculateRorForRows,
  calculateStageMarkers,
  calculateTempTicks,
  calculateXTicks,
  getAgtronClassification,
} from '../../pages/roasts/component/RoastProfileUtils';
import { useLanguage } from '../../features/language/useLanguage';
import { roasterService } from '../../services/roasterService';

interface Props {
  workOrder: WorkOrder;
}

const STAGE_KEYS: RoastStageState['key'][] = ['start', 'turning_point', 'yellow', 'first_crack', 'end'];
const STAGE_LABEL_I18N: Record<RoastStageState['key'], string> = {
  start: 'roast.profile.stages.start',
  turning_point: 'roast.profile.stages.turningPoint',
  yellow: 'roast.profile.stages.yellow',
  first_crack: 'roast.profile.stages.firstCrack',
  end: 'roast.profile.stages.end',
};

const PHASE_LABEL_I18N: Record<string, string> = {
  Drying: 'roast.profile.phases.drying',
  Maillard: 'roast.profile.phases.maillard',
  Development: 'roast.profile.phases.development',
};

const MARKER_I18N: Record<RoastStageState['key'], string | null> = {
  start: 'roast.profile.markers.charge',
  turning_point: 'roast.profile.markers.tp',
  yellow: 'roast.profile.stages.yellow',
  first_crack: 'roast.profile.markers.fcs',
  end: 'roast.profile.markers.drop',
};

// ROR is computed per 1-minute pair of 30s rows; the value lives on the middle
// row and its table cell spans down to the next 30s row (mirrors
// RoastProfileTable and the PDF's merged ROR column).
type RorCellMeta =
  | { kind: 'carrier'; span: number; ror: number | null }
  | { kind: 'covered' }
  | { kind: 'empty' };

const EXTRA_COLUMNS: { key: RoastExtraColumnKey; i18n: string }[] = [
  { key: 'airTemp', i18n: 'roast.profile.legend.airTemp' },
  { key: 'drumTemp', i18n: 'roast.profile.legend.drumTemp' },
  { key: 'environmentTemp', i18n: 'roast.profile.legend.environmentTemp' },
  { key: 'drumRpm', i18n: 'roast.profile.legend.drumRpm' },
];

export const WorkOrderRoastViewer: React.FC<Props> = ({ workOrder }) => {
  const { t } = useLanguage();

  const { data: roasters = [] } = useQuery({
    queryKey: ['roasters'],
    queryFn: () => roasterService.getAll(),
  });

  const roasterName = useMemo(() => {
    const fromId = workOrder.roasterId
      ? roasters.find((r) => r.id === workOrder.roasterId)?.name
      : undefined;
    return fromId || workOrder.roaster || '-';
  }, [roasters, workOrder.roasterId, workOrder.roaster]);

  const hasProfile =
    !!workOrder.useRoastProfile &&
    !!workOrder.roastProfile &&
    Array.isArray(workOrder.roastProfile.profile) &&
    (workOrder.roastProfile.profile as RoastProfileRow[]).length > 0;

  const rp = workOrder.roastProfile;
  // Stored profiles don't persist ROR — recompute from bean temps, same as the
  // WO form does on load and the PDF export does before rendering.
  const profile = useMemo(
    () => (hasProfile ? calculateRorForRows((rp?.profile as RoastProfileRow[]) || []) : []),
    [hasProfile, rp],
  );

  const stageStates: RoastStageState[] = useMemo(() => {
    const summary = rp?.stageSummary || {};
    return STAGE_KEYS.map((key) => {
      const entry = summary[key] || {};
      const rawTemp = entry.temp;
      const numericTemp =
        typeof rawTemp === 'string' ? Number(rawTemp) : rawTemp;
      const safeTemp: number | '' =
        numericTemp === undefined || numericTemp === null || Number.isNaN(numericTemp)
          ? ''
          : (numericTemp as number);
      return {
        key,
        label: t(STAGE_LABEL_I18N[key]),
        time: entry.time || '',
        temp: safeTemp,
      };
    });
  }, [rp, t]);

  const chartData = useMemo(
    () => calculateChartData(profile, stageStates),
    [profile, stageStates],
  );
  const maxChartTime = chartData.length > 0 ? chartData[chartData.length - 1].time : 0;

  const phaseAreas = useMemo(() => {
    return calculatePhaseAreas(stageStates, maxChartTime).map((area) => ({
      ...area,
      label: PHASE_LABEL_I18N[area.label] ? t(PHASE_LABEL_I18N[area.label]) : area.label,
    }));
  }, [stageStates, maxChartTime, t]);

  const stageMarkers = useMemo(() => {
    return calculateStageMarkers(stageStates).map((m) => {
      const key = m.key as RoastStageState['key'];
      const i18nKey = MARKER_I18N[key];
      const baseLabel = i18nKey ? t(i18nKey) : '';
      let labelBottom = baseLabel;
      if (key === 'start') labelBottom = baseLabel;
      else if (key === 'end') labelBottom = baseLabel;
      else if (baseLabel) {
        const stage = stageStates.find((s) => s.key === key);
        labelBottom = stage?.time ? `${baseLabel} ${stage.time}` : baseLabel;
      }
      return { ...m, labelBottom };
    });
  }, [stageStates, t]);

  const xTicks = useMemo(
    () => calculateXTicks(chartData, stageStates),
    [chartData, stageStates],
  );

  const maxTempFound = useMemo(() => {
    let max = 0;
    profile.forEach((r) => {
      if (r.bean && r.bean > max) max = r.bean;
      if (r.airTemp && r.airTemp > max) max = r.airTemp;
      if (r.drumTemp && r.drumTemp > max) max = r.drumTemp;
      if (r.environmentTemp && r.environmentTemp > max) max = r.environmentTemp;
      if (r.fire && r.fire > max) max = r.fire;
      if (r.airflow && r.airflow > max) max = r.airflow;
    });
    return max > 0 ? max : 200;
  }, [profile]);

  const tempAxisMax = useMemo(() => Math.ceil(maxTempFound / 20) * 20 + 20, [maxTempFound]);
  const tempTicks = useMemo(() => calculateTempTicks(tempAxisMax), [tempAxisMax]);

  const maxRightAxisValue = useMemo(() => {
    let max = 0;
    profile.forEach((r) => {
      if (typeof r.ror === 'number' && r.ror > max) max = r.ror;
      if (typeof r.drumRpm === 'number' && r.drumRpm > max) max = r.drumRpm;
    });
    return max;
  }, [profile]);

  const rightAxisMax = useMemo(() => {
    const roundedUp = Math.ceil(maxRightAxisValue / 5) * 5;
    const padded = roundedUp + 5;
    return padded > 0 ? padded : 5;
  }, [maxRightAxisValue]);

  const rightTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let tick = 0; tick <= rightAxisMax; tick += 5) {
      ticks.push(tick);
    }
    return ticks;
  }, [rightAxisMax]);

  const rorMeta = useMemo(() => {
    const meta: RorCellMeta[] = new Array(profile.length);
    let nonManualIdx = -1;
    let coveredUntilExclusive = -1;

    for (let i = 0; i < profile.length; i++) {
      const isManual = profile[i].seconds % 30 !== 0;
      if (!isManual) nonManualIdx += 1;

      if (i < coveredUntilExclusive) {
        meta[i] = { kind: 'covered' };
        continue;
      }

      if (!isManual && nonManualIdx % 2 !== 0 && nonManualIdx > 0) {
        let span = 1;
        for (let j = i + 1; j < profile.length; j++) {
          span += 1;
          if (profile[j].seconds % 30 === 0) break;
        }
        meta[i] = { kind: 'carrier', span, ror: profile[i].ror ?? null };
        coveredUntilExclusive = i + span;
      } else {
        meta[i] = { kind: 'empty' };
      }
    }
    return meta;
  }, [profile]);

  const activeExtras = useMemo(() => {
    return EXTRA_COLUMNS.filter((c) => profile.some((r) => r[c.key] != null));
  }, [profile]);

  const hasAirTempData = activeExtras.some((c) => c.key === 'airTemp');
  const hasDrumTempData = activeExtras.some((c) => c.key === 'drumTemp');
  const hasEnvironmentTempData = activeExtras.some((c) => c.key === 'environmentTemp');
  const hasDrumRpmData = activeExtras.some((c) => c.key === 'drumRpm');

  const legendItems = useMemo(() => {
    const items: { key: string; label: string; color: string }[] = [
      { key: 'bean', label: t('roast.profile.legend.beanTemp'), color: '#16a34a' },
      { key: 'ror', label: t('roast.profile.legend.ror'), color: '#eab308' },
      { key: 'fire', label: t('roast.profile.legend.fire'), color: '#dc2626' },
      { key: 'airflow', label: t('roast.profile.legend.airflow'), color: '#2563eb' },
    ];
    if (hasAirTempData) items.push({ key: 'airTemp', label: t('roast.profile.legend.airTemp'), color: '#0ea5e9' });
    if (hasDrumTempData) items.push({ key: 'drumTemp', label: t('roast.profile.legend.drumTemp'), color: '#78350f' });
    if (hasEnvironmentTempData) items.push({ key: 'environmentTemp', label: t('roast.profile.legend.environmentTemp'), color: '#a855f7' });
    if (hasDrumRpmData) items.push({ key: 'drumRpm', label: t('roast.profile.legend.drumRpm'), color: '#f97316' });
    return items;
  }, [hasAirTempData, hasDrumTempData, hasEnvironmentTempData, hasDrumRpmData, t]);

  const wIn = Number(rp?.weightAnalysis?.inputWeight ?? workOrder.inputWeight ?? 0);
  const wOut = Number(rp?.weightAnalysis?.outputWeight ?? workOrder.outputWeight ?? 0);
  const shrinkage =
    wIn > 0 && wOut > 0 ? (((wIn - wOut) / wIn) * 100).toFixed(2) : '0.00';

  const agtronWhole = Number(rp?.agtron?.wholeBean ?? 0);
  const agtronGround = Number(rp?.agtron?.groundBean ?? 0);
  const agtronDiff =
    agtronWhole > 0 && agtronGround > 0
      ? Math.abs(agtronWhole - agtronGround).toFixed(1)
      : '-';
  const classWhole = getAgtronClassification(rp?.agtron?.wholeBean ?? '');
  const classGround = getAgtronClassification(rp?.agtron?.groundBean ?? '');

  const displayedRoastType = rp?.roastType || workOrder.roastType || '';

  const beanLots = useMemo(() => {
    const items = workOrder.items || [];
    const rows: { lotNumber: string; quantity: number; beanName: string }[] = [];
    items.forEach((item) => {
      const beanName = item.name || workOrder.itemName || '-';
      (item.lots || []).forEach((l) => {
        rows.push({
          lotNumber: l.lotNumber,
          quantity: Number(l.quantity) || 0,
          beanName,
        });
      });
    });
    return rows;
  }, [workOrder.items, workOrder.itemName]);

  const totalLotWeight = useMemo(
    () => beanLots.reduce((sum, r) => sum + r.quantity, 0),
    [beanLots],
  );

  if (!hasProfile) {
    return (
      <div
        className="p-10 text-center text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300"
        data-test-id="work-order-roast-viewer-empty"
      >
        {t('cuppingSessions.form.roastViewer.noRoastProfile')}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-test-id="work-order-roast-viewer">
      {/* Header */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-white border border-gray-200 rounded-lg">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-700 font-semibold">
            {t('cuppingSessions.form.roastViewer.header.workOrder')}
          </p>
          <p className="text-sm font-semibold text-gray-900 break-all">
            {workOrder.workOrderNumber || '-'}
          </p>
          {workOrder.endDate && (
            <p className="text-xs text-gray-700 mt-0.5">
              {t('cuppingSessions.form.roastViewer.header.completed')}: {new Date(workOrder.endDate).toLocaleString()}
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-700 font-semibold">
            {t('cuppingSessions.form.roastViewer.beans.greenBean')}
          </p>
          <p className="text-sm font-semibold text-gray-900">
            {workOrder.itemName || '-'}
          </p>
          {beanLots.length === 0 ? (
            <p className="text-xs italic text-gray-500 mt-0.5">
              {t('cuppingSessions.form.roastViewer.beans.noLots')}
            </p>
          ) : (
            <ul className="mt-0.5 space-y-0.5">
              {beanLots.map((l, idx) => (
                <li
                  key={`${l.lotNumber}-${idx}`}
                  className="text-xs text-gray-700 flex items-baseline justify-between gap-2"
                >
                  <span className="font-medium truncate">{l.lotNumber}</span>
                  <span className="whitespace-nowrap">{l.quantity} kg</span>
                </li>
              ))}
              {beanLots.length > 1 && (
                <li className="text-xs font-semibold text-gray-900 flex items-baseline justify-between gap-2 border-t border-gray-200 pt-0.5">
                  <span>Total</span>
                  <span>{totalLotWeight} kg</span>
                </li>
              )}
            </ul>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-700 font-semibold">
            {t('cuppingSessions.form.roastViewer.header.machine')}
          </p>
          <p className="text-sm font-semibold text-gray-900">
            {workOrder.machine || '-'}
          </p>
          {displayedRoastType && (
            <p className="text-xs text-gray-700 mt-0.5 uppercase">
              {displayedRoastType} {t('cuppingSessions.form.roastViewer.header.roastSuffix')}
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-700 font-semibold">
            {t('cuppingSessions.form.roastViewer.header.roaster')}
          </p>
          <p className="text-sm font-semibold text-gray-900">
            {roasterName}
          </p>
        </div>
      </div>

      {/* Chart + Table grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Data Log Table */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 overflow-hidden">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
            {t('cuppingSessions.form.roastViewer.dataLog')}
          </p>
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-[11px] border-collapse">
              <thead className="sticky top-0 bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-2 py-1 font-semibold whitespace-nowrap border border-gray-200">
                    {t('cuppingSessions.form.roastViewer.table.time')}
                  </th>
                  <th className="text-center px-2 py-1 font-semibold whitespace-nowrap border border-gray-200">
                    {t('cuppingSessions.form.roastViewer.table.temp')}
                  </th>
                  <th className="text-center px-2 py-1 font-semibold whitespace-nowrap border border-gray-200">
                    {t('cuppingSessions.form.roastViewer.table.ror')}
                  </th>
                  <th className="text-center px-2 py-1 font-semibold whitespace-nowrap border border-gray-200">
                    {t('roast.profile.legend.fire')}
                  </th>
                  <th className="text-center px-2 py-1 font-semibold whitespace-nowrap border border-gray-200">
                    {t('roast.profile.legend.airflow')}
                  </th>
                  {activeExtras.map((c) => (
                    <th key={c.key} className="text-center px-2 py-1 font-semibold whitespace-nowrap border border-gray-200">
                      {t(c.i18n)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {profile.map((row, i) => {
                  const cell = rorMeta[i];
                  let rorCell: React.ReactNode = null;
                  if (cell.kind === 'carrier') {
                    rorCell = (
                      <td
                        className="px-2 py-1 text-center align-middle bg-gray-50 border border-gray-200 font-medium"
                        rowSpan={cell.span}
                      >
                        {cell.ror ?? '-'}
                      </td>
                    );
                  } else if (cell.kind === 'empty') {
                    rorCell = <td className="px-2 py-1 text-center bg-gray-50 border border-gray-200" />;
                  }
                  return (
                    <tr
                      key={`${row.seconds}-${i}`}
                      className={row.seconds % 30 !== 0 ? 'bg-amber-50' : ''}
                    >
                      <td className="px-2 py-1 font-mono border border-gray-200">{row.time}</td>
                      <td className="px-2 py-1 text-center border border-gray-200">{row.bean ?? '-'}</td>
                      {rorCell}
                      <td className="px-2 py-1 text-center border border-gray-200">{row.fire ?? '-'}</td>
                      <td className="px-2 py-1 text-center border border-gray-200">{row.airflow ?? '-'}</td>
                      {activeExtras.map((c) => (
                        <td key={c.key} className="px-2 py-1 text-center border border-gray-200">
                          {row[c.key] ?? '-'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Chart + Analysis */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
              {t('cuppingSessions.form.roastViewer.profileCurve')}
            </p>
            <RoastProfileChart
              chartData={chartData}
              legendItems={legendItems}
              phaseAreas={phaseAreas}
              xTicks={xTicks}
              tempAxisMax={tempAxisMax}
              tempTicks={tempTicks}
              rightAxisMax={rightAxisMax}
              rightTicks={rightTicks}
              hasAirTempData={hasAirTempData}
              hasDrumTempData={hasDrumTempData}
              hasEnvironmentTempData={hasEnvironmentTempData}
              hasDrumRpmData={hasDrumRpmData}
              stageMarkers={stageMarkers}
            />
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
              <Metric
                label={t('cuppingSessions.form.roastViewer.analysis.in')}
                value={wIn.toFixed(2)}
                unit="kg"
              />
              <Metric
                label={t('cuppingSessions.form.roastViewer.analysis.out')}
                value={wOut.toFixed(2)}
                unit="kg"
              />
              <Metric
                label={t('cuppingSessions.form.roastViewer.analysis.loss')}
                value={shrinkage}
                unit="%"
                valueClassName="text-red-700"
              />
              <Metric
                label={t('cuppingSessions.form.roastViewer.analysis.whole')}
                value={String(rp?.agtron?.wholeBean || '-')}
                subtopText={agtronWhole > 0 ? classWhole.scaa : undefined}
                subbottomText={agtronWhole > 0 ? classWhole.common : undefined}
              />
              <Metric
                label={t('cuppingSessions.form.roastViewer.analysis.ground')}
                value={String(rp?.agtron?.groundBean || '-')}
                subtopText={agtronGround > 0 ? classGround.scaa : undefined}
                subbottomText={agtronGround > 0 ? classGround.common : undefined}
              />
              <Metric
                label={t('cuppingSessions.form.roastViewer.analysis.diffType')}
                value={agtronDiff}
                subtopText={displayedRoastType ? `${displayedRoastType.toUpperCase()} ${t('cuppingSessions.form.roastViewer.header.roastSuffix').toUpperCase()}` : undefined}
              />
            </div>
          </div>

          {workOrder.internalNotes && (
            <div className="bg-gray-50 border-l-4 border-gray-300 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wide text-gray-700 font-semibold mb-1">
                {t('cuppingSessions.form.roastViewer.notes')}
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {workOrder.internalNotes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface MetricProps {
  label: string;
  value: string;
  unit?: string;
  subtopText?: string;
  subbottomText?: string;
  valueClassName?: string;
}

const Metric: React.FC<MetricProps> = ({ label, value, unit, subtopText, subbottomText, valueClassName }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-gray-700 font-semibold">{label}</p>
    <div className="flex items-baseline justify-center gap-1">
      <span className={`text-lg font-bold text-gray-900 ${valueClassName || ''}`}>{value}</span>
      {unit && <span className="text-xs text-gray-700">{unit}</span>}
    </div>
    {subtopText && <p className="text-[10px] text-black uppercase font-medium">{subtopText}</p>}
    {subbottomText && <p className="text-[10px] text-black uppercase font-medium">{subbottomText}</p>}
  </div>
);
